import React, { useState, useRef, useEffect } from 'react';
import { SkipForward, Volume2, VolumeX, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CinematicIntro = ({ onComplete }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  // Handle "Enter" button - starts video with its own audio
  const handleEnter = async () => {
    setHasStarted(true);
    
    const video = videoRef.current;
    if (video) {
      video.muted = false; // Play with video's own audio
      try {
        await video.play();
      } catch (e) {
        console.log("Video play failed:", e);
        // If autoplay with sound fails, try muted
        video.muted = true;
        setIsMuted(true);
        try {
          await video.play();
        } catch (e2) {
          console.log("Muted play also failed:", e2);
        }
      }
    }
  };

  // Track video progress
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  };

  // Handle video end
  const handleVideoEnd = () => {
    setTimeout(() => onComplete(), 500);
  };

  // Skip button
  const handleSkip = () => {
    if (videoRef.current) videoRef.current.pause();
    onComplete();
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black">
      {/* Video Player */}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover transition-opacity duration-500 ${hasStarted && videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        onCanPlay={() => setVideoLoaded(true)}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleVideoEnd}
        onError={() => {
          console.log("Video error");
          setTimeout(() => onComplete(), 1500);
        }}
        playsInline
        preload="auto"
      >
        <source src={`${API_URL}/api/uploads/files/redacted_promo.mp4`} type="video/mp4" />
      </video>

      {/* Initial Screen - Before clicking Enter */}
      {!hasStarted && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center animate-fade-in">
            {/* Logo */}
            <div className="mb-8">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-red-500/30 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-red-500/50 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-300">M</span>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-white to-red-500 tracking-wider">
                MINDESIGN
              </h1>
              <p className="text-gray-500 text-sm tracking-[0.3em] mt-2">ENTERPRISES</p>
            </div>

            {/* Enter Button */}
            <button
              onClick={handleEnter}
              data-testid="enter-intro-btn"
              className="group relative px-12 py-4 bg-transparent border-2 border-red-500 text-white rounded-full font-bold text-lg transition-all duration-500 hover:bg-red-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(239,68,68,0.5)]"
            >
              <span className="flex items-center space-x-3">
                <Play size={24} className="group-hover:scale-110 transition-transform" />
                <span>ENTER THE FORGE</span>
              </span>
            </button>
            
            <p className="text-gray-600 text-xs mt-6 tracking-wide">CLICK TO EXPERIENCE WITH SOUND</p>
          </div>
        </div>
      )}

      {/* Overlay Controls - shown after starting */}
      {hasStarted && (
        <>
          {/* Progress Bar at bottom */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 w-64">
            <div className="h-1 bg-gray-800/50 rounded-full overflow-hidden backdrop-blur-sm">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-red-300 transition-all duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Top Right Controls */}
          <div className="absolute top-6 right-6 flex space-x-3">
            {/* Mute Button */}
            <button
              onClick={toggleMute}
              className="w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-accent-red transition-all duration-300 hover:scale-110"
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>

            {/* Skip Button */}
            <button
              onClick={handleSkip}
              className="h-12 px-4 bg-black/50 backdrop-blur-sm rounded-full flex items-center space-x-2 text-white hover:bg-accent-red transition-all duration-300"
            >
              <span className="text-sm font-medium">Skip</span>
              <SkipForward size={18} />
            </button>
          </div>
        </>
      )}

      {/* Loading state if video not ready but started */}
      {hasStarted && !videoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-red-500/30 rounded-full animate-ping"></div>
              <div className="absolute inset-0 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-500">Loading experience...</p>
          </div>
        </div>
      )}
    </div>
  );
};
